import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { apiClient } from '../services/api'
import type { UserResponse } from '../types/api'

interface AuthContextType {
  user: UserResponse | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true)
      const userData = await apiClient.getCurrentUser()
      setUser(userData)
    } catch (error: any) {
      // Not authenticated or session expired
      if (error.status === 401) {
        setUser(null)
      } else {
        console.error('Auth check failed:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await apiClient.login(username, password)
      setUser(response.user)
    } catch (error) {
      setUser(null)
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiClient.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setUser(null)
    }
  }, [])

  // Setup global error handler for 401 errors
  useEffect(() => {
    const handleUnauthorized = () => {
      console.log('Unauthorized access detected - logging out')
      setUser(null)
    }

    apiClient.setUnauthorizedHandler(handleUnauthorized)
  }, [])

  // Check authentication status on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
